(function () {
  'use strict'

  /**
     * @ngdoc module
     * @name thismomentFormlyForm
     * @module thismoment.common.formly.form
     *
     * @param {string} cancelText Text string for cancel button (default: 'cancel')
     * @param {array} fields See: http://docs.angular-formly.com/docs/field-configuration-object
     * @param {object} formData an object that should be bound to the component; acts as the model of the form
     * @param {function} onCancel Callback executed on form cancel
     * @param {function} onSubmit Callback executed on form submit
     * @param {object} options See: http://docs.angular-formly.com/docs/formly-form#section-options
     * @param {string} submitText Text string for submit button (default: 'submit')
     *
     * @description
     * One form to rule them all. Add some formly form to your component, keep it DRY.
     */

  const MODULE_NAME = 'thismoment.common.formly.form'
  const templateUrl = require('./tpl/formly.html')

  require('../scss/loader.scss')
  require('./scss/formly.scss')
  require('./formly.tpl.html')

  require('angular')
    .module(MODULE_NAME, [
      require('angular-sanitize'),
      require('angular-formly'),
      require('angular-formly-templates-bootstrap'),
      require('./config/messages')
    ])
    .config(thismomentFormlyFormConfig)
    .component('thismomentFormlyForm', {
      templateUrl: templateUrl,
      bindings: {
        autocomplete: '<',
        cancelBackgroundColor: '<',
        cancelColor: '<',
        cancelText: '<',
        fields: '<',
        formData: '=',
        options: '<',
        onCancel: '<',
        onSubmit: '<',
        submitBackgroundColor: '<',
        submitColor: '<',
        submitText: '<'
      },
      controller: thismomentFormlyForm
    })

  thismomentFormlyForm.$inject = ['$window', '$rootScope']
  function thismomentFormlyForm ($window, $rootScope) {
    const vm = this

    vm.$onInit = thismomentFormlyFormInit

    function thismomentFormlyFormInit () {
      vm.formSubmit = formSubmit

      vm.cancelText = vm.cancelText || 'cancel'
      vm.submitText = vm.submitText || 'submit'

      vm.styles = {
        cancel: {
          'background-color': vm.cancelBackgroundColor,
          color: vm.cancelColor
        },
        submit: {
          'background-color': vm.submitBackgroundColor,
          color: vm.submitColor
        }
      }

      $rootScope.$on('formly:runExpressions', () => {
        mapFormlyFields(vm.fields, runFormlyFieldExpressions, false)
      })

      $rootScope.$on('formly:resetForm', () => {
        vm.form.$setPristine()
        vm.form.$setUntouched()
      })

      mapFormlyFields(vm.fields, addDynamicAttributes, false)

      function formSubmit () {
        mapFormlyFields(vm.fields, runFormlyFieldExpressions, true)
        if (vm.form.$valid) {
          if (angular.isDefined($window.grecaptcha) && angular.isDefined($window.grecaptchaKey)) {
            $window.grecaptcha.ready(function () {
              $window.grecaptcha.execute($window.grecaptchaKey, {action: 'submit'}).then(function (token) {
                vm.formData.recaptcha_token_v3 = token
                vm.onSubmit()
              })
            })
          } else {
            vm.onSubmit()
          }
        } else {
          const firstErrorEl = findFirstErrorEl(vm.fields)
          if (firstErrorEl) {
            firstErrorEl.focus()
          }
        }
      }

      /*
       * @param {array} fields : an array of formly field definitions
       *
       * @description
       * Iterates through an array of formly field definitions and
       * locates the first invalid field. Returns the DOM element.
       *
       * @returns {object} DOM element
       */
      function findFirstErrorEl (fields) {
        let firstErrorField = null
        let el = null

        for (let i = 0; i < fields.length; i++) {
          if (fields[i].validation && fields[i].validation.errorExistsAndShouldBeVisible) {
            firstErrorField = fields[i]
            break
          } else if (fields[i].fieldGroup && fields[i].fieldGroup.length > 0) {
            firstErrorField = findFirstErrorEl(fields[i].fieldGroup)
            if (firstErrorField) {
              break
            }
          }
        }

        if (firstErrorField) {
          // Check if firstErrorField is already a DOM element
          if (firstErrorField instanceof HTMLElement) {
            el = firstErrorField
          } else if (firstErrorField.formControl && firstErrorField.formControl.$$element) {
            el = firstErrorField.formControl.$$element[0]
          }

          // If el is not an input, try to find the file-picker
          if (el && el.tagName !== 'INPUT') {
            const filePicker = el.getElementsByClassName('file-picker')[0]
            if (filePicker) {
              el = filePicker
            }
          }
        }

        return el
      }

      /*
       * @param {object} field : a formly field definition
       *
       * @description
       * Adds logic for the formly field configs to apply
       * `aria-invalid` and `aria-required` attributes to the field
       * when the following two conditions apply:
       * (1) the input is invalid (e.g. there is no input)
       * (2) the field is blurred OR the form is submitted
       */
      function addDynamicAttributes (field) {
        const doesFormlyFieldErrorExist = function ($viewValue, $modelValue, scope) {
          return scope.options.validation.errorExistsAndShouldBeVisible
        }
        const getErrorLabelIfErrorPresent = function ($viewValue, $modelValue, scope) {
          return scope.options.validation.errorExistsAndShouldBeVisible ? scope.options.key + '_error' : null
        }

        if (field.hasOwnProperty('templateOptions') && field.templateOptions.required) {
          field.templateOptions.onBlur = function ($viewValue, $modelValue, scope) {
            scope.options.runExpressions()
          }
          if (typeof field.ngModelAttrs === 'undefined') {
            field.ngModelAttrs = {}
          }
          field.ngModelAttrs.ariaInvalid = {
            attribute: 'aria-invalid'
          }
          field.ngModelAttrs.ariaRequired = {
            attribute: 'aria-required'
          }
          field.ngModelAttrs.ariaDescribedby = {
            attribute: 'aria-describedby'
          }
          if (typeof field.expressionProperties === 'undefined') {
            field.expressionProperties = {}
          }
          field.expressionProperties['templateOptions.ariaRequired'] = 'true'
          field.expressionProperties['templateOptions.ariaDescribedby'] = getErrorLabelIfErrorPresent
          field.expressionProperties['templateOptions.ariaInvalid'] = doesFormlyFieldErrorExist
        }
      }

      /*
       * @param {object} field : a formly field definition
       *
       * @description
       * Executes the field's expressions
       */
      function runFormlyFieldExpressions (field) {
        if (angular.isFunction(field.runExpressions)) {
          field.runExpressions()
        }
      }

      /*
       * @param {object} formlyFields : a collection of formly field configs
       * @param {function} func : a function to execute on field configs
       * @param {boolean} skipHiddenFields : do not execute func on hidden fields
       *
       * @description
       * Executes the func against an array of formly fields
       */
      function mapFormlyFields (formlyFields, func, skipHiddenFields) {
        formlyFields.map(function (field, idx) {
          if (skipHiddenFields && field.hasOwnProperty('hide')) {

          } else if (field.hasOwnProperty('fieldGroup')) {
            // recursion!
            mapFormlyFields(field.fieldGroup, func, skipHiddenFields)
          } else {
            // only execute this on actual fields (i.e. not wrappers, etc)
            if (field.hasOwnProperty('key')) {
              func(field)
            }
          }
        })
      }
    }
  }

  thismomentFormlyFormConfig.$inject = ['formlyConfigProvider']
  function thismomentFormlyFormConfig (formlyConfigProvider) {
    formlyConfigProvider.disableWarnings = true
  }

  module.exports = MODULE_NAME
})()



// WEBPACK FOOTER //
// ./src/common/formly/index.js